import { useEffect, useState } from 'react';

import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';

import CashPendingSalesList from '../../components/Cash/pending-sales/list/CashPendingSalesList';

import CashPendingSalesToolbar from '../../components/Cash/pending-sales/toolbar/CashPendingSalesToolbar';

import POSLayout from '../../layout/POSLayout';

import { PendingSalesViewMode } from '../../types/cash/pendingCashSale.types';

import { useSale } from '../../hooks/sale/useSale';

import { useSalesButtonActions } from '../../hooks/sale/useSalesButtonConfig';

import { useBranch } from '../../hooks/branches/useBranch';

import { useAuthUser } from '../../hooks/auth/useAuthUser';

import { formatCurrency } from '../../utils/formatCurrency';

import SaleViewModal from '../Sale/history/SaleViewModal';

import POSProcessPendingCashierSaleModal from '../../components/Sales/pos/modal/POSProcessPendingCashierSaleModal';
import { PendingCashierSaleCardDTO } from '../../types/sale/dto/PendingCashierSaleCardDTO';
import MultiplePaymentModal from '../../components/Sales/MultiplePayment/MultiplePaymentModal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

const CashPendingSalesPage = () => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const {
    pendingCashierSales,

    handlerGetPendingCashierSales,

    handlerViewSaleById,

    saleToViewLoaded,

    handleCloseViewModal,

    showPendingCashierModal,

    closePendingCashierModal,

    handlerLoadPendingCashierSale,

    showMultiplePaymentModal,

    closeMultiPaymentModal,

    form,
  } = useSale();

  // Método existente de cancelación de venta pendiente (history-actions).
  const { handlerCancelPendingSale } = useSalesButtonActions();

  const { hasRole } = useAuthUser();

  // Solo administrador y soporte pueden anular una venta pendiente de cobro.
  const canCancelPendingSale = hasRole('ADMIN') || hasRole('SUPPORT');

  const {
    branchDropdown,

    fetchBranchDropdown,
  } = useBranch();

  const pendingCashierRefreshCounter = useSelector(
    (state: RootState) => state.sale.pendingCashierRefreshCounter
  );

  // =====================================================
  // 🔹 State
  // =====================================================

  const [viewMode, setViewMode] = useState<PendingSalesViewMode>('list');

  const [branchId, setBranchId] = useState<number | undefined>();

  const [sellerId, setSellerId] = useState<number | undefined>();

  const [search, setSearch] = useState('');

  // =====================================================
  // 🔹 Load Branches
  // =====================================================

  useEffect(() => {
    fetchBranchDropdown();
  }, []);

  // =====================================================
  // 🔹 Default Branch
  // =====================================================

  useEffect(() => {
    if (!branchId && branchDropdown.length > 0) {
      setBranchId(branchDropdown[0].id);
    }
  }, [branchDropdown]);

  // =====================================================
  // 🔹 Load Sales
  // =====================================================

  useEffect(() => {
    if (!branchId) {
      return;
    }

    console.log('🔄 Refrescando ventas pendientes caja');

    handlerGetPendingCashierSales(branchId, sellerId);
  }, [branchId, sellerId, pendingCashierRefreshCounter]);

  // =====================================================
  // 🔹 Process Sale
  // =====================================================

  const handleProcessSale = async (sale: PendingCashierSaleCardDTO) => {
    await handlerLoadPendingCashierSale(sale.saleId);
  };

  const handleCancelSale = (sale: PendingCashierSaleCardDTO) => {
    // Reutiliza el método existente: POST /api/sales/history-actions/cancel/{saleId}
    handlerCancelPendingSale(sale.saleId);
  };

  // =====================================================
  // 🔹 Resumen para el cajero
  // =====================================================

  const pendingCount = pendingCashierSales.length;

  const pendingTotal = pendingCashierSales.reduce(
    (sum, sale) => sum + (sale.total || 0),
    0
  );

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <POSLayout>
      <BreadcrumbTwo />

      <div className="space-y-5">
        <CashPendingSalesToolbar
          viewMode={viewMode}
          onChangeView={setViewMode}
          sellerId={sellerId}
          onSellerChange={setSellerId}
          search={search}
          onSearchChange={setSearch}
        />

        {/* ===================================================== */}
        {/* 🔹 Resumen: cuántas ventas y cuánto falta cobrar */}
        {/* ===================================================== */}

        {pendingCount > 0 && (
          <div
            className="
              flex flex-wrap items-center justify-between gap-4

              rounded-2xl border border-slate-200 bg-white px-5 py-4

              shadow-sm

              dark:border-strokedark dark:bg-boxdark
            "
          >
            <div className="flex items-center gap-3">
              <span
                className="
                  flex h-10 min-w-10 items-center justify-center

                  rounded-xl bg-primary/10 px-2

                  text-base font-bold text-primary
                "
              >
                {pendingCount}
              </span>

              <div className="leading-tight">
                <p className="text-sm font-semibold text-slate-800 dark:text-white">
                  {pendingCount === 1
                    ? 'venta pendiente de cobro'
                    : 'ventas pendientes de cobro'}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Selecciona una y presiona <strong>Cobrar</strong>.
                </p>
              </div>
            </div>

            <div className="text-right">
              <p className="text-xs text-slate-400">Total por cobrar</p>
              <p className="text-2xl font-bold text-primary">
                ${formatCurrency(pendingTotal)}
              </p>
            </div>
          </div>
        )}

        <CashPendingSalesList
          sales={pendingCashierSales}
          viewMode={viewMode}
          onViewSale={handlerViewSaleById}
          onProcessSale={handleProcessSale}
          canCancel={canCancelPendingSale}
          onCancelSale={handleCancelSale}
        />
      </div>

      {/* ===================================================== */}
      {/* 🔹 View Modal */}
      {/* ===================================================== */}

      <SaleViewModal isOpen={saleToViewLoaded} onClose={handleCloseViewModal} />

      {/* ===================================================== */}
      {/* 🔹 Process Cashier Modal */}
      {/* ===================================================== */}

      <POSProcessPendingCashierSaleModal
        isOpen={showPendingCashierModal}
        onClose={closePendingCashierModal}
      />

      {/* 🔹 Multiple Payments */}
      <MultiplePaymentModal
        isOpen={showMultiplePaymentModal}
        onClose={closeMultiPaymentModal}
        customerName={form.customer?.name}
      />
    </POSLayout>
  );
};

export default CashPendingSalesPage;
